// import Vue from 'vue';
import {
  createWebHistory,
  createRouter,
  type RouteRecordRaw,
  type RouteRecordSingleView,
  type RouteRecordRedirect,
  createMemoryHistory,
} from 'vue-router';
import * as routes from './routes';
import config from '@/config';
// pages
import Search from './pages/search/search.vue';
import Listing from './pages/listing/listing.vue';
import Unsubscribe from './pages/verify/unsubscribe.vue';
import Condominium from './pages/listing/condominium.vue';
import Agency from './pages/agencies/agency.vue';
import CorporateAgency from './pages/agencies/company-agency.vue';
import Agent from './pages/agent/agent.vue';
import ErrorPage from './pages/error/error.vue';
import Static from './pages/static/static.vue';
import SEO from './pages/seo/seo.vue';
import TemporaryLogin from './pages/temporary-login/temporary-login.vue';
import utils from '@/app/utils/utils';
import CurrentNewsItem from './pages/current-news-item/current-news-item.vue';
import isSSR from '@/utils/isSSR';
import { BasicUrlSchema, SearchUrlSchema } from '@/schemas/urlSchema';
import LandingPage from './pages/landingPage.vue';
import logger from '@/app/logger';
import { getRouteParamAsString } from './composables/useSafeRoute';
import { regionShortnameSchema } from '@/schemas/regionDataSchema';

const routeMap: RouteRecordRaw[] = [
  // // SEO Paths development
  ...routes.search.sale.routes
    .filter((route) => !route.notSearchRoute) // Filter out other than search pages
    .flatMap(
      (route) =>
        [
          {
            path: route.path,
            component: Search,
            beforeEnter(to, from, next) {
              if (!utils.parseRouteQuery(to.query, SearchUrlSchema)) {
                next(routes.error.path);
              } else {
                next();
              }
            },
          },
          {
            path: `${route.path}/:cityName`,
            component: Search,
            beforeEnter(to, from, next) {
              if (!utils.parseRouteQuery(to.query, SearchUrlSchema)) {
                next(routes.error.path);
              } else {
                next();
              }
            },
          },
        ] as RouteRecordSingleView[]
    ),
  ...routes.search.rental.routes
    .filter((route) => !route.hasOwnProperty('exclude'))
    .flatMap(
      (route) =>
        [
          {
            path: route.path,
            component: Search,
            beforeEnter(to, from, next) {
              if (!utils.parseRouteQuery(to.query, SearchUrlSchema)) {
                next(routes.error.path);
              } else {
                next();
              }
            },
          },
          {
            path: `${route.path}/:cityName`,
            component: Search,
            beforeEnter(to, from, next) {
              if (!utils.parseRouteQuery(to.query, SearchUrlSchema)) {
                next(routes.error.path);
              } else {
                next();
              }
            },
          },
        ] as RouteRecordSingleView[]
    ),
  {
    // THese redirects are due to mergers or new names for cities/towns
    path: '/kiinteistonvalitys/nummela',
    beforeEnter(to, from, next) {
      next('/kiinteistonvalitys/vihti');
    },
  } as RouteRecordRedirect,
  {
    path: '/kiinteistonvalitys/luvia',
    beforeEnter(to, from, next) {
      next('/kiinteistonvalitys/eurajoki');
    },
  } as RouteRecordRedirect,
  {
    path: '/kiinteistonvalitys/klaukkala',
    beforeEnter(to, from, next) {
      next('/kiinteistonvalitys/nurmijarvi');
    },
  } as RouteRecordRedirect,
  {
    path: '/kiinteistonvalitys/karjaa',
    beforeEnter(to, from, next) {
      next('/kiinteistonvalitys/raasepori');
    },
  } as RouteRecordRedirect,
  {
    path: '/kiinteistonvalitys/kemio',
    beforeEnter(to, from, next) {
      next('/kiinteistonvalitys/kemionsaari');
    },
  } as RouteRecordRedirect,
  {
    path: '/kiinteistonvalitys/mantta',
    beforeEnter(to, from, next) {
      next('/kiinteistonvalitys/mantta-vilppula');
    },
  } as RouteRecordRedirect,
  {
    path: '/kiinteistonvalitys/sirkka',
    beforeEnter(to, from, next) {
      next('/kiinteistonvalitys/kittila');
    },
  } as RouteRecordRedirect,
  {
    path: '/kiinteistonvalitys/vaaksy',
    beforeEnter(to, from, next) {
      next('/kiinteistonvalitys/asikkala');
    },
  } as RouteRecordRedirect,
  {
    path: '/kiinteistonvalitys/tammisaari',
    beforeEnter(to, from, next) {
      next('/kiinteistonvalitys/raasepori');
    },
  } as RouteRecordRedirect,
  {
    path: '/henkilotietojen-kasittely-valitus',
    beforeEnter(to, from, next) {
      next('/henkilotietojen-kasittely');
    },
  } as RouteRecordRedirect,
  {
    path: routes.componentCatalog.path,
    component: LandingPage,
    beforeEnter(to, from, next) {
      if (isSSR && config.env !== 'development' && config.env !== 'local') {
        next(routes.error.path);
      } else if (
        !isSSR &&
        window?.location?.hostname !== 'dev.op-koti.fi' &&
        window?.location?.hostname !== 'localhost'
      ) {
        next(routes.error.path);
      } else {
        next();
      }
    },
  },
  {
    path: routes.SEO.path,
    component: SEO,
  },
  {
    path: routes.allOptimizelyComponents.path,
    component: LandingPage,
    beforeEnter(to, from, next) {
      if (isSSR && config.env !== 'development' && config.env !== 'local') {
        next(routes.error.path);
      } else if (
        !isSSR &&
        window?.location?.hostname !== 'dev.op-koti.fi' &&
        window?.location?.hostname !== 'localhost'
      ) {
        next(routes.error.path);
      } else {
        next();
      }
    },
  },
  {
    path: `${routes.unsubscribe.path}/:id?`,
    component: Unsubscribe,
  },
  {
    path: `${routes.listing.path}/:id/vuokra-asuntohakemus`,
    component: () => import('./pages/rent-application/rent-application.vue'),
  },
  {
    path: `${routes.listing.path}/:id`,
    alias: `${routes.listing.path}/:id/*`,
    component: Listing,
    beforeEnter(to, from, next) {
      const id = getRouteParamAsString(to.params.id) || '';
      if (id.length === 6 && !!utils.parseRouteQuery(to.query, BasicUrlSchema)) {
        next();
      } else {
        next(routes.error.path);
      }
    },
  },
  {
    path: `${routes.condominium.path}/:id`,
    component: Condominium,
  },
  {
    // toimialue == business area page, it's now deprecated so redirect to the correct page to fix google hits
    path: `${routes.agencies.path}/toimialue/:id`,
    beforeEnter(to, from, next) {
      const id = getRouteParamAsString(to.params.id);
      next(`${routes.agencies.path}/${id}`);
    },
  } as RouteRecordRedirect,
  {
    path: `${routes.currentNewsItem.path}/:id`,
    component: CurrentNewsItem,
  },
  {
    path: `${routes.agencies.path}/:id`, // /kiinteistonvalitys/:id
    component: Agency,
    beforeEnter(to, _, next) {
      const id = getRouteParamAsString(to.params.id) || '';
      const result = regionShortnameSchema.safeParse(id);
      if (result.success) {
        next();
      } else {
        next(routes.error.path);
      }
    },
  },
  {
    path: `${routes.agencies.path}/yhtio/:id`,
    component: CorporateAgency,
  },
  {
    path: '/valittaja/:agentId',
    component: Agent,
  },
  {
    path: `${routes.agencies.path}/:agencyId/:agentId`,
    component: Agent,
  },
  {
    path: routes.staticPage.paths.cookies,
    component: Static,
  },
  {
    path: `${routes.priceCalculator.path}/:tab?`,
    component: () => import('./pages/price-calculator/price-calculator.vue'),
  },
  {
    path: routes.error.path,
    component: ErrorPage,
    beforeEnter: (to, from, next) => {
      // Commented out because it's not logical to go to search in case of any error. Only in case of listing not found,
      // but in that case also, not found page is clearer for user.
      // Also, this causes two next() calls for which Vue throws a warning.
      //
      // // if the listing doesnt exist or work for some reason, redirect the user to the front page of apartments for sale
      //   if (from.path.includes(routes.listing.path)) {
      //     next(routes.apartmentsForSale.path);
      //   }
      next();
    },
  },
  {
    path: routes.embeddable.path,
    component: Search,
  },
  {
    path: routes.contactPage.path,
    component: LandingPage,
    beforeEnter: (to, from, next) => {
      // Set the default contact tab based on the previous page
      if (from.path.includes(routes.rentingLandingPage.path)) {
        to.params.selectedRequestType = utils.contactRequestTypes.LEASE_OFFERED;
      }
      next();
    },
  },
  {
    path: routes.temporaryLoginLinks.path,
    component: TemporaryLogin,
  },
  {
    path: routes.whyRealtor.path,
    beforeEnter(to, from, next) {
      next(routes.agencies.path);
    },
  } as RouteRecordRedirect,
  {
    // TODO this is a temporary redirect for some TV campaign
    path: '/kinonkulma',
    beforeEnter(to, from, next) {
      next('/uudiskohde/as-oy-akaan-kinon-kulma?cityLD=20');
    },
  } as RouteRecordRedirect,
  {
    path: '/sb/:id?',
    alias: '/storybook/:id?',
    component: () => import('./pages/storybook/storybook.vue'),
    beforeEnter(to, from, next) {
      if (isSSR && config.env !== 'development' && config.env !== 'local') {
        next(routes.error.path);
      } else if (
        !isSSR &&
        window?.location?.hostname !== 'dev.op-koti.fi' &&
        window?.location?.hostname !== 'localhost'
      ) {
        next(routes.error.path);
      } else {
        next();
      }
    },
  },
  {
    path: routes.home.path,
    component: LandingPage,
  },
  {
    // This matches the root path
    path: '/:allLandingPages',
    component: LandingPage,
    beforeEnter(to, from, next) {
      next();
    },
    children: [
      {
        // This matches nested paths
        path: ':subPage(.*)',
        component: LandingPage, // or another component if needed
        beforeEnter(to, from, next) {
          next();
        },
      },
    ],
  },
];

const router = createRouter({
  history: isSSR ? createMemoryHistory() : createWebHistory(),
  routes: routeMap,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    } else if (savedPosition) {
      return savedPosition;
    } else {
      if (to.path === from.path) {
        return { left: window.pageXOffset, top: window.pageYOffset };
      } else {
        return { top: 0 };
      }
    }
  },
});
router.beforeEach((to, from, next) => {
  logger.info(`Navigating to: "${to.path}"`);
  next();
});

export default router;
